/* global ShopgateProductDetailsCore, pageInformation */
import ClientInformation from '../client-information/ClientInformation';
import Logger from '../../common/debug/tagged-logger';

/**
 * AmazonPay native SDK implementation
 *
 * @link https://shopgate.atlassian.net/wiki/spaces/SPEC/pages/142885953/Amazon+Login+and+Pay
 */
class AbstractAmazonPay {
  /**
   * REGION_EU static.
   * @returns {string}
   */
  static get REGION_EU() {
    return 'EU';
  }

  /**
   * Region US static.
   * @returns {string}
   */
  static get REGION_US() {
    return 'NA';
  }

  /**
   * Region UK static.
   * @returns {string}
   */
  static get REGION_UK() {
    return 'UK';
  }
  /**
   * Constructor
   *
   * @param {Object} opts Options.
   * @param {jQuery} $ JQuery.
   * @param {Object} SGEvent SGEven.
   * @param {Object} SGAction SGAction.
   * @param {Object} sgData sgData.
   * @param {Object|null} sgWebStorage sgWebStorage.
   */
  constructor(opts, $, SGEvent, SGAction, sgData, sgWebStorage = null) {
    this.$ = $;
    this.SGAction = SGAction;
    this.SGEvent = SGEvent;
    this.sgData = sgData;
    this.options = opts;
    this.amazonOrderReferenceId = null;
    this.namespace = 'AmazonPay';
    this.clientInformation = new ClientInformation(sgWebStorage);
    this.logger = new Logger('AMAZON_PAY');

    this.handleAmazonPayDidStart = this.handleAmazonPayDidStart.bind(this);
    this.handleAmazonDidLogOut = this.handleAmazonDidLogOut.bind(this);
    this.handleUserLoggedInStateChange = this.handleUserLoggedInStateChange.bind(this);
  }

  /**
   * Returns user error codes constants.
   * @return {{USER_DID_CANCEL: string, AUTHORIZATION_ERROR: string}}
   */
  get errorCodes() {
    return {
      USER_DID_CANCEL: 'SGUserDidCancel',
      AUTHORIZATION_ERROR: 'SGAuthorizationError',
    };
  }

  /**
   * Current currency getter
   * @return {*|string}
   */
  get currency() {
    const marketId = this.sgData.shop.market_id;
    const currencyLookup = {
      US: 'USD',
      PL: 'PLN',
      CH: 'CHF',
    };

    return currencyLookup[marketId] || 'EUR';
  }

  /**
   * Current region getter
   * @return {*|string}
   */
  get region() {
    const marketId = this.sgData.shop.market_id;

    const regionLookup = {
      DE: this.constructor.REGION_EU,
      FR: this.constructor.REGION_EU,
      PL: this.constructor.REGION_EU,
      AT: this.constructor.REGION_EU,
      CH: this.constructor.REGION_EU,
      UK: this.constructor.REGION_UK,
      US: this.constructor.REGION_US,
    };

    return regionLookup[marketId] || this.constructor.REGION_EU;
  }

  /**
   * Scopes depending on the market.
   * For US there cannot be paymentBillingAddress.
   * @returns {Array}
   */
  getScopes() {
    const scopes = [
      'profileProfile',
      'paymentShippingAddress',
      'paymentBillingAddress',
    ];
    if (this.region !== this.constructor.REGION_US) {
      scopes.push('paymentInstrument');
    }

    return scopes;
  }
  /**
   * Checks if amazon pay is supported on this device.
   *
   * @return {boolean}
   */
  isAmazonPaySupported() {
    return this.clientInformation.hasAmazonPay();
  }

  /**
   * Shows general error. Can be used for showing
   * user as vague feedback.
   */
  showGeneralError() {
    this.$(document)
      .trigger('sgFramework.message.system', [{
        message: $.i18n._('payment_general_try_again'),
        state: 'message_error',
        display_behaviour: 'popup',
      }]);
  }

  /**
   * Shows general payment failed error.
   */
  showPaymentFailedError() {
    this.$(document)
      .trigger('sgFramework.message.system', [{
        message: $.i18n._('payment_general_failed'),
        state: 'message_error',
        display_behaviour: 'popup',
      }]);
  }

  /**
   * Start amazon pay (prepare SDK with region, etc)
   */
  amazonPayStart() {
    this.SGAction.amazonPayStart({
      scopes: this.getScopes(),
      region: this.region,
      ledgerCurrency: this.currency,
    });
    this.amazonPayDidStarted();
  }

  /**
   * Set serial when amazon pay was started
   */
  amazonPayDidStarted() {
    window.localStorage.setItem(
      'AmazonPay.started',
      window.localStorage.getItem('AppEventRegistry.appStarted')
    );
  }

  /** Log user out from Amazon */
  logUserOut = () => this.SGAction.amazonLogOut();

  /** Reload checkout */
  reloadCheckout = () => this.SGEvent.Broadcast.reloadCheckout();

  /**
   * Starts the checkout process by requesting the backend endpoint.
   *
   * @param {Object} response - amazonOrderReferenceId, accessToken
   */
  handleAmazonPayDidStart(response) {
    if (response.errorCode === this.errorCodes.USER_DID_CANCEL) {
      this.$.shopgate.spinner('hide');
      if (typeof ShopgateProductDetailsCore !== 'undefined') {
        ShopgateProductDetailsCore.setStateCartButtonBusy(false);
      }
      return;
    }

    if (!(pageInformation && pageInformation.visible)) {
      this.$.shopgate.spinner('hide');
      return;
    }
    /**
     * Error callback
     */
    const onErrorCb = () => {
      this.SGAction.amazonLogOut();
      if (typeof ShopgateProductDetailsCore !== 'undefined') {
        ShopgateProductDetailsCore.setStateCartButtonBusy(false);
      }
      this.showGeneralError();
    };

    if (response.errorCode) {
      if (response.errorCode !== this.errorCodes.AUTHORIZATION_ERROR) {
        onErrorCb();
      }
      return;
    }

    const accessToken = response.accessToken;
    const source = this.sgData.page.link;
    this.amazonOrderReferenceId = response.amazonOrderReferenceId;
    const postData = {
      amazonOrderReferenceId: this.amazonOrderReferenceId,
      accessToken,
      source,
    };
    const isItemPage = this.sgData.page.name === 'productDetails';
    const isCartPage = this.sgData.page.name === 'cart';
    const isCheckoutPage = this.sgData.page.name === 'checkout';
    const isCheckoutPaymentShippingPage = this.sgData.page.name === 'checkoutPaymentAndShipping - payment';

    if (isItemPage) {
      postData.selected_values = ShopgateProductDetailsCore.getSelectedValues();
    }

    const onSuccessCb = () => {
      if (isItemPage || isCartPage) {
        // Shame timeout to avoid address synchro mismatch
        this.SGAction.popTabToRoot({
          targetTab: 'cart',
        });
        this.SGAction.openPage(
          {
            src: 'sgapi:checkout',
            targetTab: 'cart',
          });
        this.SGAction.showTab({
          targetTab: 'cart',
        });
      } else if (!isCheckoutPage) {
        if (isCheckoutPaymentShippingPage) {
          this.SGAction.popTab({
            targetTab: 'cart',
          });
        }
        this.SGEvent.Broadcast.reloadCart();
        this.SGEvent.Broadcast.reloadCheckout();
      }
    };

    this.$.shopgate.ajax({
      url: 'sgapi:ajax_amazon_pay_checkout_start',
      type: 'POST',
      data: postData,
      success: (data) => {
        if (!data.success) {
          onErrorCb();
          return;
        }
        onSuccessCb();
      },
      error: onErrorCb,
      complete: () => {
        if (!isItemPage) {
          return;
        }
        ShopgateProductDetailsCore.setStateCartButtonBusy(false);
        this.$.shopgate.spinner('hide');
      },
    });
  }

  handleAmazonDidLogOut() {
    this.SGEvent.Broadcast.reloadCart();
    this.SGEvent.Broadcast.reloadCheckout();
  }

  /**
   * Clears the amazon sdk session on user state change.
   */
  handleUserLoggedInStateChange() {
    this.logUserOut();
  }

  /**
   * Register App userLoggedEvents
   */
  registerEvents() {
    this.SGEvent.Broadcast.register(
      'amazonPayDidStart',
      'amazonDidLogOut'
    );

    this.SGEvent
      .unregisterCallback(`amazonPayDidStart.${this.namespace}`)
      .registerCallback(`amazonPayDidStart.${this.namespace}`, this.handleAmazonPayDidStart);
  }

  /**
   * @param {Object} postData
   * @param {function} cb cb(err, response)
   */
  getOrderDetails(postData, cb) {
    this.$.shopgate.ajax({
      url: 'sgapi:ajax_amazon_get_order',
      type: 'POST',
      data: postData,
      success: (response) => {
        if (!response.success) {
          return cb(new Error('Not success'));
        }
        cb(null, response.data);
      },
      error: err => cb(new Error(err))
    });
  }
}

export default AbstractAmazonPay;



// WEBPACK FOOTER //
// ./Library/1.0/app/amazon-pay/AbstractAmazonPay.js